import { useEffect, useState } from 'react';
import { captureSupabaseError } from '../lib/supabaseError';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';
import type { Broadcast } from '../types';

interface UseBroadcastResult {
  broadcast: Broadcast | null;
  loading: boolean;
  error: string | null;
}

export function useBroadcast(
  broadcastId: string | null | undefined,
): UseBroadcastResult {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!client || !ready || !broadcastId) {
      setBroadcast(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data, error: err } = await client
          .from('broadcasts_admin_view')
          .select('*')
          .eq('id', broadcastId)
          .maybeSingle();
        if (cancelled) return;
        if (err) {
          captureSupabaseError(err, {
            action: 'fetch_broadcast',
            extra: { broadcast_id: broadcastId },
          });
          setError(err.message);
          setBroadcast(null);
        } else {
          setBroadcast((data as Broadcast | null) ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, ready, broadcastId]);

  return { broadcast, loading, error };
}
